import * as Sentry from '@sentry/react';
import { ILogger } from './ILogger';
import { isAutomatedTests } from '../utils/isAutomatedTests';

const sensitiveKeys = [ 'accesstoken', 'password' ];

export class Logger implements ILogger {

    // set by Network, when offline
    // to avoid circular dependency
    public skipSendingToSentry  = false;

    private recursivelyScrubSensitiveKeys = (data: any) => {
        if (!data || typeof data !== 'object') {
            return data;
        }
        const result = { ...data };
        for (const key of Object.keys(result)) {
            if (sensitiveKeys.includes(key.toLowerCase())) {
                result[key] = '***';
            } else {
                result[key] = this.recursivelyScrubSensitiveKeys(data[key]);
            }
        }
        return result;
    };

    private consoleLog(...args: any[]) {
        // "undefined" / "null" in logs can be distracting/confusing
        console.log(...args.filter(a => a !== undefined && a !== null));
    }

    private log = (message: string, extras: any) => {
        try {
            extras = this.recursivelyScrubSensitiveKeys(extras);

            this.consoleLog(message, extras);

        } catch (ignore) {
            // do nothing so that logging isn't the cause of app failures
        }
    };

    error = (error: any, extras?: any) => {
        try {
            extras = this.recursivelyScrubSensitiveKeys(extras);

            this.consoleLog(error, extras);

            const is400 = error && error.status && error.status >= 400 && error.status < 500;
            const isDevFlagSimulation = error?.errorCode === 'dev-flag-simulation' && isAutomatedTests();

            // don't send 400s to sentry, as thse are expected errors (eg validation)
            // don't send errors that happen while offline, as these are likely a result
            //  of being offline and therefore aren't actionable
            // don't send dev flag simulation errors, as these are intentional test errors
            if (!is400 && !this.skipSendingToSentry && !isDevFlagSimulation) {
                Sentry.withScope(scope => {
                    if (extras) {
                        Object.keys(extras).forEach(key => {
                            scope.setExtra(key, extras[key]);
                        });
                    }
                    // A string reaches Sentry stackless and grouped by its text. Wrapped in Error, as the api does,
                    // native errors would group by minified frames instead, since no source maps are uploaded.
                    Sentry.captureException(error);
                });
            }
        } catch (ignore) {
            // don't ever want to throw anything from this class!
        }
    };

    info = (message: string, extras?: any) => {
        this.log(message, extras);
    };
}
